import { SupplierRoutePath } from './routes';
// Module-scope helpers (not components) — a caller inside a component passes
// its bound `t` (useTranslation('freeSupplier')) for a live-reactive value;
// the i18n singleton default keeps non-React callers working unchanged, same
// pattern as helpers/validationMessages.js's resolveValidationMessage.
import i18n from 'i18n';

// Publishing a factor runs a profile-completeness gate on the backend. When it
// fails, the response body carries a MACHINE-READABLE breakdown, not just a
// sentence:
//
//   {
//     code: 'SUPPLIER_PUBLICATION_REQUIREMENTS_NOT_MET',
//     message: 'Supplier publishing requires … Missing: corporate contact …',
//     blockers: [{ field, code, requirement, message }, …]
//   }
//
// A toast renders `message` alone, which (a) truncates the moment there is
// more than one blocker and (b) never says WHERE to fix anything. These helpers
// turn the payload into something the review surface can render in full.
export const SUPPLIER_PUBLICATION_REQUIREMENTS_CODE = 'SUPPLIER_PUBLICATION_REQUIREMENTS_NOT_MET';

// Where a blocker gets fixed, derived from its `field` PREFIX rather than from
// its `code`. The prefix is the stable part of the contract (`operator.*` is
// company profile, `installation.*` is an installation), while the code set is
// owned by the backend and will grow — matching on codes would silently drop
// the link for every future blocker, whereas an unknown prefix here just
// degrades to "no link" and still shows the requirement + explanation.
const buildFixTargets = (t) => [
  {
    prefix: 'operator.',
    to: SupplierRoutePath.Company,
    label: t('freeSupplier:publicationBlockers.fixInMyCompany', 'Fix in My Company'),
  },
  {
    prefix: 'installation.',
    to: SupplierRoutePath.Installations,
    label: t('freeSupplier:publicationBlockers.fixInInstallations', 'Fix in Installations'),
  },
];

export const blockerFixTarget = (field, t = i18n.t.bind(i18n)) => {
  if (typeof field !== 'string') return null;
  return buildFixTargets(t).find((target) => field.startsWith(target.prefix)) || null;
};

// The payload's own `message` trails a "Missing: a, b" recap of the blockers
// we are about to list individually. Strip it so the headline states the
// requirement once and the list carries the detail — otherwise every blocker
// reads twice.
const stripMissingRecap = (message) =>
  typeof message === 'string' ? message.replace(/\s*Missing:.*$/s, '').trim() : '';

// null unless this really is the publication-requirements failure WITH at
// least one blocker to show. Everything else (overlap rejections, 409s,
// network errors) keeps its existing handling untouched.
//
// Reads `payload` (SupplierApiError) and `response.data` (raw axios) so it
// works regardless of which layer surfaced the error.
export const parsePublicationBlockers = (error, t = i18n.t.bind(i18n)) => {
  const payload = error?.payload || error?.response?.data;
  if (!payload || typeof payload !== 'object') return null;
  if (payload.code !== SUPPLIER_PUBLICATION_REQUIREMENTS_CODE) return null;
  if (!Array.isArray(payload.blockers) || payload.blockers.length === 0) return null;

  return {
    summary:
      stripMissingRecap(payload.message) ||
      t(
        'freeSupplier:publicationBlockers.defaultSummary',
        'Your supplier profile is missing something publishing requires.',
      ),
    blockers: payload.blockers
      // A blocker with neither a requirement nor a message would render as an
      // empty bullet — drop it rather than show a blank row.
      .filter((blocker) => blocker && (blocker.requirement || blocker.message))
      .map((blocker) => ({
        field: blocker.field ?? null,
        code: blocker.code ?? null,
        requirement:
          blocker.requirement ||
          t('freeSupplier:publicationBlockers.missingInformation', 'Missing information'),
        message: blocker.message || '',
        fixTarget: blockerFixTarget(blocker.field, t),
      })),
  };
};
